import React, { useEffect, useState } from 'react';
import { Button, FloatingNotification } from '@wix/design-system';
import { useTranslation } from 'react-i18next';
import { StatusAlert } from '@wix/wix-ui-icons-common';
import Header from '@components/Header';
import { ACTIONS, useAppContext } from '@components/Contexts/AppContext';
import useFeedbackApi from '@src/components/Hooks/useFeedbackApi';
import SiteRenderer from '@components/SiteRenderer';
import LoadingScreen from '@components/LoadingScreen';
import DisableTool from '@components/DisableTool';
import useBiLogger from '@components/Hooks/useBiLogger';
import s from './App.scss';
import '@wix/design-system/styles.global.css';
import { ANONYMOUS } from '@src/utils/constants';
import createBiLogger from '@src/utils/biLogger';
import {
  getNewAppClientUrl,
  hasNewAppComments,
} from '@src/utils/newAppRedirect';

export default function App() {
  const { t } = useTranslation();
  const { logPageLoaded } = useBiLogger();
  const { getInitializeData } = useFeedbackApi();
  const [commentsFetchPending, setCommentsFetchPending] =
    useState<boolean>(true);

  const {
    dispatch,
    state: {
      errorData,
      loading,
      viewerElement,
      disableTool,
      metaSiteId,
      siteUrl,
      feedbackId,
    },
  } = useAppContext();
  useEffect(() => {
    logPageLoaded();
  }, [logPageLoaded]);

  useEffect(() => {
    (async () => {
      try {
        const {
          comments: _comments,
          userInfo,
          visitorId,
        } = (await getInitializeData()) || {};
        if (!userInfo || !_comments) {
          return;
        }
        if (hasNewAppComments(_comments)) {
          window.location.replace(
            getNewAppClientUrl({
              siteUrl,
              feedbackId,
              search: window.location.search,
            }),
          );
          return;
        }
        if (userInfo.userName === ANONYMOUS) {
          userInfo.isAnonymous = true;
        }
        if (!visitorId) {
          dispatch({ type: ACTIONS.DISABLE_TOOL });
          return;
        }
        dispatch({
          type: ACTIONS.SET_BI_LOGGER,
          payload: createBiLogger(visitorId, metaSiteId),
        });
        dispatch({
          type: ACTIONS.SET_COMMENTS,
          payload: _comments,
        });
        dispatch({
          type: ACTIONS.SET_COMMENTS_FETCH_PENDING,
          payload: false,
        });
        dispatch({
          type: ACTIONS.SET_USER_INFO,
          payload: userInfo,
        });
        setCommentsFetchPending(false);
      } catch (e) {
        dispatch({
          type: ACTIONS.SET_ERROR,
          payload: {
            showError: true,
            errorMsgLocalKey: 'get_feedback_error',
          },
        });
        dispatch({
          type: ACTIONS.SET_USER_INFO,
          payload: { isAnonymous: true, userName: '', contactId: '' },
        });
        setCommentsFetchPending(false);
      }
    })();
  }, []);

  return (
    <div data-hook="app">
      {(loading || commentsFetchPending) && <LoadingScreen />}
      {disableTool ? (
        <DisableTool />
      ) : (
        <>
          <Header />
          {errorData.showError && (
            <FloatingNotification
              onClose={() => {
                dispatch({
                  type: ACTIONS.SET_ERROR,
                  payload: { showError: false },
                });
              }}
              className={s.errorNotification}
              type="destructive"
              textButtonProps={{
                label: (
                  <Button size="tiny" skin="dark" priority="secondary">
                    {t('get_feedback_error_cta')}
                  </Button>
                ),
                onClick: () => {
                  if (viewerElement?.contentDocument) {
                    viewerElement.contentDocument.location.reload();
                  } else {
                    window.location.reload();
                  }
                  dispatch({
                    type: ACTIONS.SET_ERROR,
                    payload: { showError: false },
                  });
                  dispatch({ type: ACTIONS.SET_LOADING, payload: true });
                },
              }}
              prefixIcon={<StatusAlert />}
              text={t(errorData.errorMsgLocalKey)}
              width="fit-content"
            />
          )}
          <SiteRenderer />
        </>
      )}
    </div>
  );
}
